
import React from 'react';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';

export default function ProductsSection() {
  const formats = [
    {
      icon: 'ri-capsule-line',
      title: 'Oxygen Capsules',
      subtitle: 'Internal Oxygen Support',
      description: 'Oxygen capsules are distributed through community health networks to individuals identified with oxygen deficiency, supporting cellular health and daily vitality.',
      features: ['Supports cellular oxygen levels', 'Distributed via healthcare networks', 'Guided by health professionals', 'Safe for daily use'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/89e3b8cbae433baaede37ee316ae083c.jfif',
    },
    {
      icon: 'ri-drop-line',
      title: 'Oxygen Sprays & Skin Products',
      subtitle: 'Topical Oxygen Access',
      description: 'Topical oxygen sprays and skin formulations made available through PHCs and ASHA workers to address skin-related oxygen deficiency conditions in communities.',
      features: ['Topical oxygen delivery', 'Supports skin health', 'Community health worker distribution', 'Complementary to medical care'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/891eb307d91497f1e13065bb15119a8e.jfif',
    },
    {
      icon: 'ri-heart-line',
      title: 'OxyKits for Families & Communities',
      subtitle: 'Bundled Community Access',
      description: 'OxyKits are comprehensive access bundles containing capsules and sprays, designed for families and communities identified through DLF\'s screening programs.',
      features: ['Capsules + sprays combined', 'Designed for family use', 'Distributed through community programs', 'Affordable access model'],
      image: 'https://public.readdy.ai/ai/img_res/64658fddb54c47b6f2afda40b8dddb53.jpg',
    }
  ];

  return (
    <section id="products-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-6">
          <span className="inline-block text-[#12B6B6] font-semibold text-sm uppercase tracking-widest mb-3">Access Formats</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            How DLF Enables Supplementation Access
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            DLF works through healthcare and community networks to make oxygen supplementation reachable — not as a commercial product, but as a public health initiative.
          </p>
        </div>

        {/* Disclaimer Banner */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="border-l-4 border-[#12B6B6] pl-5 py-3 bg-[#12B6B6]/5 rounded-r-xl">
            <p className="text-sm text-gray-600 italic leading-relaxed">
              Our supplementation initiatives are designed to complement conventional medical care and should be undertaken in consultation with healthcare professionals.
            </p>
          </div>
        </div>

        {/* Formats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {formats.map((format, index) => (
            <div
              key={index}
              className="group bg-[#FAFAFA] rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-white to-gray-50 flex items-center justify-center p-6">
                <img
                  src={format.image}
                  alt={format.title}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-8 bg-white">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#12B6B6]/10">
                    <i className={`${format.icon} text-2xl text-[#12B6B6]`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#111111]">{format.title}</h3>
                    <p className="text-sm text-[#12B6B6] font-medium">{format.subtitle}</p>
                  </div>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">{format.description}</p>

                <div className="space-y-2 mb-6">
                  {format.features.map((feature, fi) => (
                    <div key={fi} className="flex items-center space-x-3">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-[#12B6B6]"></i>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link to="/donate" className="block">
                  <Button variant="primary" size="sm" className="w-full">
                    Support This Initiative
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Info Strip */}
        <div className="bg-gradient-to-r from-[#12B6B6]/5 to-[#12B6B6]/10 rounded-3xl p-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-[#111111] mb-4">
              All Supplementation Access is Community & Healthcare-Guided
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              DLF does not sell products. All supplementation formats are distributed through verified healthcare and community networks as part of our public health mission.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-2">
                <i className="ri-community-line text-[#12B6B6]"></i>
                <span className="text-sm font-medium">Community Networks</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-hospital-line text-[#12B6B6]"></i>
                <span className="text-sm font-medium">Healthcare-Guided</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-shield-check-line text-[#12B6B6]"></i>
                <span className="text-sm font-medium">Safety First</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-government-line text-[#12B6B6]"></i>
                <span className="text-sm font-medium">Section-8 Non-Profit</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
